import type { ReactNode } from 'react'

import { FiKey, FiPlayCircle } from 'react-icons/fi'

import { Dialog } from '@loadsmart/miranda-react'

import opendockIcon from 'assets/images/opendock_icon.png'
import shipperguideIcon from 'assets/images/shipperguide_icon.png'

import { OpendockWordmark, ShipperGuideWordmark } from './brandMarks'
import {
  StyledFootnote,
  StyledIntro,
  StyledOption,
  StyledOptionDetails,
  StyledOptionHead,
  StyledOptionName,
  StyledOptionTile,
  StyledOptionTitle,
} from './SignInHelpDialog.styles'

interface SignInHelpDialogProps {
  open: boolean
  onClose: () => void
  isShipperGuideEnabled: boolean
  isOpendockEnabled: boolean
}

interface SignInOption {
  key: string
  heading: ReactNode
  icon: ReactNode
  who: string
  how: string
  seeing: string
}

const SHIPPERGUIDE_OPTION: SignInOption = {
  key: 'shipperguide',
  heading: (
    <StyledOptionName>
      <ShipperGuideWordmark />
    </StyledOptionName>
  ),
  icon: <img src={shipperguideIcon} alt="" />,
  who: 'Anyone with a ShipperGuide login — shipper teams and the Loadsmart people who support them.',
  how: 'Uses your existing ShipperGuide session. No extra password, and no separate portal account to request.',
  seeing:
    'Freight spend, lane and carrier performance, tender and quote history — limited to the accounts you can already see in ShipperGuide.',
}

const OPENDOCK_OPTION: SignInOption = {
  key: 'opendock',
  heading: (
    <StyledOptionName>
      <OpendockWordmark />
    </StyledOptionName>
  ),
  icon: <img src={opendockIcon} alt="" />,
  who: 'Warehouse and facility teams who schedule appointments in Opendock.',
  how: 'Uses your existing Opendock session, with the same warehouse permissions you already have there.',
  seeing:
    'Appointment volume, dwell time, on-time arrivals, no-shows and throughput, broken down by facility and dock.',
}

const BASIC_OPTION: SignInOption = {
  key: 'basic',
  heading: <StyledOptionTitle>Email and password</StyledOptionTitle>,
  icon: <FiKey color="rgb(var(--m-color-brand-accent-1))" />,
  who: 'Standalone portal accounts — people who need analytics but have no ShipperGuide or Opendock login.',
  how: 'Credentials issued directly by the Analytics team. If you have a product login, use that instead — it stays in sync with your permissions.',
  seeing: 'Only the dashboards explicitly granted to your account.',
}

const DEMO_OPTION: SignInOption = {
  key: 'demo',
  heading: <StyledOptionTitle>Demo</StyledOptionTitle>,
  icon: <FiPlayCircle color="rgb(var(--m-color-brand-accent-1))" />,
  who: 'Anyone without an account, or anyone who wants to see the portal before bringing their own data in.',
  how: 'Open, no sign-in. Leave whenever you like.',
  seeing: 'The same dashboards, running on sample freight and dock data.',
}

// Lists only the options the page is actually offering, in the same order.
const SignInHelpDialog = ({
  open,
  onClose,
  isShipperGuideEnabled,
  isOpendockEnabled,
}: SignInHelpDialogProps) => {
  const options = [
    ...(isShipperGuideEnabled ? [SHIPPERGUIDE_OPTION] : []),
    ...(isOpendockEnabled ? [OPENDOCK_OPTION] : []),
    BASIC_OPTION,
    DEMO_OPTION,
  ]

  return (
    <Dialog open={open} size="medium" variant="neutral" onClose={onClose}>
      <Dialog.Header>
        Which sign-in should I use?
        <Dialog.Close />
      </Dialog.Header>
      <Dialog.Body>
        <StyledIntro>Your access follows the product your account already lives in.</StyledIntro>

        {options.map(option => (
          <StyledOption key={option.key}>
            <StyledOptionHead>
              <StyledOptionTile aria-hidden="true">{option.icon}</StyledOptionTile>
              {option.heading}
            </StyledOptionHead>
            <StyledOptionDetails>
              <dt>Who</dt>
              <dd>{option.who}</dd>
              <dt>How</dt>
              <dd>{option.how}</dd>
              <dt>You&rsquo;ll see</dt>
              <dd>{option.seeing}</dd>
            </StyledOptionDetails>
          </StyledOption>
        ))}

        <StyledFootnote>
          Still not sure? Try your product sign-in first — if it doesn&rsquo;t recognise you, reach
          out to the Analytics team.
        </StyledFootnote>
      </Dialog.Body>
    </Dialog>
  )
}

export default SignInHelpDialog
